import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES } from "@/data/services";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services", children: SERVICES },
    { to: "/blog", label: "Insights" },
    { to: "/news", label: "News" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-[#DCE1E8]/95 backdrop-blur-xl border-b border-dr-silver ${
                scrolled ? "shadow-[0_4px_24px_rgba(8,41,74,0.10)]" : "shadow-none"
            }`}
        >
            <div className="dr-container flex items-center justify-between h-40">
                <Link to="/" data-testid="nav-logo" className="flex items-center group">
                    <img
                        src="/logo-color.png"
                        alt="Dynamic Risks — Assess. Adapt. Protect."
                        className="h-36 w-auto object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV.map((item) => (
                        item.children ? (
                            <div
                                key={item.to}
                                className="relative"
                                onMouseEnter={() => setServicesOpen(true)}
                                onMouseLeave={() => setServicesOpen(false)}
                            >
                                <NavLink
                                    to={item.to}
                                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `dr-underline text-[0.92rem] font-medium tracking-wide inline-flex items-center gap-1 ${
                                            isActive ? "text-dr-navy" : "text-dr-navy/80"
                                        } hover:text-dr-navy`
                                    }
                                >
                                    {item.label}
                                    <ChevronDown className="w-3.5 h-3.5" />
                                </NavLink>
                                {servicesOpen && (
                                    <div
                                        data-testid="services-dropdown"
                                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[360px]"
                                    >
                                        <div className="bg-white border border-dr-silver rounded-2xl shadow-[0_20px_60px_rgba(8,41,74,0.14)] p-3">
                                            {item.children.map((s) => (
                                                <Link
                                                    key={s.slug}
                                                    to={`/services/${s.slug}`}
                                                    data-testid={`nav-service-${s.slug}`}
                                                    className="block px-4 py-3 rounded-xl hover:bg-dr-grey transition-colors"
                                                >
                                                    <div className="text-sm font-semibold text-dr-navy font-display">{s.title}</div>
                                                    <div className="text-xs text-dr-dark/70 mt-1 leading-relaxed">{s.short}</div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`nav-link-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `dr-underline text-[0.92rem] font-medium tracking-wide ${
                                        isActive ? "text-dr-navy" : "text-dr-navy/80"
                                    } hover:text-dr-navy`
                                }
                            >
                                {item.label}
                            </NavLink>
                        )
                    ))}
                </nav>

                <Link
                    to="/contact"
                    data-testid="nav-cta-speak"
                    className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-dr-navy text-white text-sm font-medium hover:bg-dr-blue transition-colors"
                >
                    Speak to an expert
                </Link>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-dr-silver text-dr-navy"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-dr-silver">
                    <div className="dr-container py-6 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`mobile-link-${item.label.toLowerCase()}`}
                                className="py-3 border-b border-dr-silver/60 text-dr-navy font-medium"
                            >
                                {item.label}
                            </Link>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-cta-speak"
                            className="mt-6 inline-flex justify-center px-5 py-3 rounded-full bg-dr-navy text-white font-medium"
                        >
                            Speak to an expert
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
